import { ChatMarkdown } from "@/core/components/chat-markdown";
import { Stack } from "@volca/ui/components/ui/stack";
import { EditorResult } from "@/payroll/editor/editor-result";
import { RequestOutcome } from "@/payroll/editor/editor-result/request-outcome";
import type { EnrichedMessage } from "@/payroll/editor/types";
import { AssistantReply } from "./assistant-reply";
import { OwnerMessage } from "./owner-message";
import { TurnActivity } from "./turn-activity";
import { TurnFailure } from "./turn-failure";
export const TranscriptMessage = ({
  message,
  disabled,
  onReply,
  onWriteOwn,
  onResend,
  /** Whether anything can be sent at all. Not whether this is the newest. */
  canResend,
  animateReply,
  stopping,
}: {
  message: EnrichedMessage;
  disabled: boolean;
  onReply: (value: string) => void;
  onWriteOwn: () => void;
  onResend: (request: string) => void;
  canResend: boolean;
  animateReply: boolean;
  stopping: boolean;
}) => (
  <div className="min-w-0 wrap-break-word">
    {message.result ? (
      <EditorResult
        result={message.result}
        onReply={onReply}
        onWriteOwn={onWriteOwn}
        disabled={disabled}
      />
    ) : (
      message.role !== "tool" &&
      // The customer's own words read as a sent message; the
      // assistant answers in the body of the page, the way every chat
      // the owner already uses behaves.
      (message.role === "user" ? (
        <>
          <OwnerMessage attachments={message.attachments}>
            {message.content}
          </OwnerMessage>
          <TurnActivity
            activity={message.activity}
            active={
              message.activity?.status === "running" ||
              message.activity?.status === "queued" ||
              message.activity?.status === "waiting"
            }
            sending={false}
            stopping={stopping}
          />
          {message.outcome && (
            <RequestOutcome
              outcome={message.outcome}
              request={message.content}
              onResend={onResend}
              disabled={!canResend}
            />
          )}
        </>
      ) : (
        <Stack
          gap={0}
          align="stretch"
          direction="column"
          className="min-w-0 text-sm text-ink"
        >
          {message.role === "assistant" &&
          !message.content.trim() &&
          !message.hasRequestOutcome ? (
            <TurnFailure
              note="The assistant returned no reply. Review the proposal before you send another request."
              request={message.requestContent}
              detail="A payroll rules request finished with no reply at all."
              onResend={onResend}
              disabled={!canResend}
            />
          ) : (
            <>
              {message.role === "assistant" ? (
                <AssistantReply
                  content={message.content}
                  streaming={!!message.streaming}
                  animate={animateReply}
                />
              ) : (
                <ChatMarkdown className="text-xs text-subtle">
                  {message.content}
                </ChatMarkdown>
              )}
              {/* The reply already asks for the request again, so the button
                  belongs under it. The server marks this ending; the wording
                  is not what decides. */}
              {message.retryable && !message.hasRequestOutcome && (
                <TurnFailure
                  request={message.requestContent}
                  detail="A payroll rules request ran out of steps before it could save anything."
                  onResend={onResend}
                  disabled={!canResend}
                />
              )}
            </>
          )}
        </Stack>
      ))
    )}
  </div>
);
